import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../services/api';

interface XeroIntegrationProps {
  reportData: any;
  filters: any;
}

interface XeroConnection {
  isConnected: boolean;
  organisationName?: string;
  connectionId?: string;
  expiresAt?: string;
}

interface InvoicePreview {
  client: string;
  projectBreakdown: Array<{
    projectName: string;
    hours: number;
    rate: number;
    amount: number;
  }>;
  totalHours: number;
  totalAmount: number;
  currency: string;
}

const XeroIntegration: React.FC<XeroIntegrationProps> = ({ reportData, filters }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const [xeroConnection, setXeroConnection] = useState<XeroConnection>({ isConnected: false });
  const [isConnecting, setIsConnecting] = useState(false);
  const [invoicePreviews, setInvoicePreviews] = useState<InvoicePreview[]>([]);
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [isCreatingInvoices, setIsCreatingInvoices] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    reference: '',
    includeTimesheetDetails: true,
    groupByProject: true
  });

  useEffect(() => {
    // TODO: Enable when Xero backend is implemented
    // checkXeroConnection();
    if (reportData) {
      generateInvoicePreviews();
    }
  }, [reportData]);

  const checkXeroConnection = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/xero/connection`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const contentType = response.headers.get('content-type');
      if (response.ok && contentType?.includes('application/json')) {
        const data = await response.json();
        setXeroConnection(data);
      }
    } catch {
      // Silently ignore - endpoint not yet implemented
    }
  };

  const connectToXero = async () => {
    setIsConnecting(true);
    try {
      // This would initiate the OAuth flow with Xero
      const response = await fetch(`${API_BASE_URL}/xero/auth`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Xero OAuth URL
        window.location.href = data.authUrl;
      } else {
        throw new Error('Failed to initiate Xero connection');
      }
    } catch (error) {
      console.error('Failed to connect to Xero:', error);
      showToast(t('xero.connectFailed'), 'error');
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectXero = async () => {
    try {
      await fetch(`${API_BASE_URL}/xero/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setXeroConnection({ isConnected: false });
    } catch (error) {
      console.error('Failed to disconnect from Xero:', error);
    }
  };

  const generateInvoicePreviews = () => {
    if (!reportData || !reportData.summary) return;

    const previews: InvoicePreview[] = [];
    
    Object.entries(reportData.summary).forEach(([clientName, clientData]: [string, any]) => {
      if (clientName === 'No Client') return; // Skip entries without clients

      const projectBreakdown = Object.entries(clientData.projects).map(([projectName, projectData]: [string, any]) => ({
        projectName,
        hours: projectData.total_hours,
        rate: projectData.total_billable_amount / projectData.total_hours || 0,
        amount: projectData.total_billable_amount
      }));

      previews.push({
        client: clientName,
        projectBreakdown,
        totalHours: clientData.total_hours,
        totalAmount: clientData.total_billable_amount,
        currency: 'CAD' // This could be configurable per client
      });
    });

    setInvoicePreviews(previews);
    // Select all clients by default
    setSelectedClients(new Set(previews.map(p => p.client)));
  };

  const handleClientSelection = (client: string, selected: boolean) => {
    const newSelection = new Set(selectedClients);
    if (selected) {
      newSelection.add(client);
    } else {
      newSelection.delete(client);
    }
    setSelectedClients(newSelection);
  };

  const createXeroInvoices = async () => {
    if (selectedClients.size === 0) {
      showToast(t('xero.selectClientRequired'), 'warning');
      return;
    }

    setIsCreatingInvoices(true);
    try {
      const selectedPreviews = invoicePreviews.filter(p => selectedClients.has(p.client));
      
      const invoiceData = {
        invoices: selectedPreviews.map(preview => ({
          client: preview.client,
          invoiceDate: invoiceSettings.invoiceDate,
          dueDate: invoiceSettings.dueDate,
          reference: invoiceSettings.reference,
          lineItems: invoiceSettings.groupByProject 
            ? preview.projectBreakdown.map(project => ({
                description: `${project.projectName} - Professional Services`,
                quantity: project.hours,
                unitAmount: project.rate,
                accountCode: '4000', // This should be configurable
                taxType: 'OUTPUT' // This should be configurable based on jurisdiction
              }))
            : [{
                description: `Professional Services - ${formatDateRange()}`,
                quantity: preview.totalHours,
                unitAmount: preview.totalAmount / preview.totalHours,
                accountCode: '4000',
                taxType: 'OUTPUT'
              }],
          includeTimesheetDetails: invoiceSettings.includeTimesheetDetails,
          timesheetData: reportData.time_entries?.filter((entry: any) => 
            entry.project?.client?.name === preview.client
          )
        })),
        filters
      };

      const response = await fetch(`${API_BASE_URL}/xero/invoices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invoiceData)
      });

      if (response.ok) {
        const result = await response.json();
        showToast(t('xero.invoicesCreated', { count: result.created_count }), 'success');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create invoices');
      }
    } catch (error) {
      console.error('Failed to create Xero invoices:', error);
      showToast(t('xero.invoicesFailed'), 'error');
    } finally {
      setIsCreatingInvoices(false);
    }
  };

  const formatDateRange = () => {
    if (!filters.start_date || !filters.end_date) return 'Time Period';
    const start = new Date(filters.start_date).toLocaleDateString();
    const end = new Date(filters.end_date).toLocaleDateString();
    return start === end ? start : `${start} - ${end}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50'
    : 'bg-white border border-slate-200 shadow-sm';
  const inputClass = isDark
    ? 'w-full px-3 py-2 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:outline-none focus:ring-violet-500 focus:border-violet-500'
    : 'w-full px-3 py-2 bg-white border border-slate-200 text-slate-900 rounded-xl focus:outline-none focus:ring-violet-500 focus:border-violet-500';

  if (!reportData) {
    return (
      <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
        <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Generate a report first to create Xero invoices</p>
      </div>
    );
  }

  return (
    <div className={`${cardClass} rounded-xl p-6 mt-6`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img
            src="https://developer.xero.com/assets/img/xero-logo.svg"
            alt="Xero"
            className="h-8 w-auto mr-3"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Xero Integration</h3>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {xeroConnection.isConnected
                ? `Connected to ${xeroConnection.organisationName}`
                : 'Create draft invoices in Xero from your time entries'
              }
            </p>
          </div>
        </div>

        {!xeroConnection.isConnected ? (
          <button
            onClick={connectToXero}
            disabled={isConnecting}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50 flex items-center text-sm font-semibold"
          >
            {isConnecting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Connect to Xero
              </>
            )}
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={createXeroInvoices}
              disabled={isCreatingInvoices || selectedClients.size === 0}
              className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200 disabled:opacity-50 flex items-center text-sm font-semibold"
            >
              {isCreatingInvoices ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Invoices...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Create Draft Invoices ({selectedClients.size})
                </>
              )}
            </button>
            <button
              onClick={disconnectXero}
              className={`px-3 py-2 rounded-xl border transition-colors ${isDark ? 'text-slate-400 hover:text-slate-200 border-slate-600 hover:border-slate-500' : 'text-slate-600 hover:text-slate-800 border-slate-300 hover:border-slate-400'}`}
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {xeroConnection.isConnected && invoicePreviews.length > 0 && (
        <div className="space-y-6">
          {/* Invoice Settings */}
          <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
            <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Invoice Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  Invoice Date
                </label>
                <input
                  type="date"
                  value={invoiceSettings.invoiceDate}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, invoiceDate: e.target.value }))}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  Due Date
                </label>
                <input
                  type="date"
                  value={invoiceSettings.dueDate}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, dueDate: e.target.value }))}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  Reference (Optional)
                </label>
                <input
                  type="text"
                  value={invoiceSettings.reference}
                  onChange={(e) => setInvoiceSettings(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="e.g., PO123456"
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={invoiceSettings.groupByProject}
                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, groupByProject: e.target.checked }))}
                    className={`rounded ${isDark ? 'border-slate-600 bg-slate-800' : 'border-slate-300'} text-violet-600 focus:ring-violet-500`}
                  />
                  <span className={`ml-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Group by project</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={invoiceSettings.includeTimesheetDetails}
                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, includeTimesheetDetails: e.target.checked }))}
                    className={`rounded ${isDark ? 'border-slate-600 bg-slate-800' : 'border-slate-300'} text-violet-600 focus:ring-violet-500`}
                  />
                  <span className={`ml-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Include timesheet details</span>
                </label>
              </div>
            </div>
          </div>

          {/* Invoice Previews */}
          <div>
            <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Invoice Preview</h4>
            <div className="space-y-4">
              {invoicePreviews.map((preview, index) => (
                <div key={index} className={`border rounded-xl p-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedClients.has(preview.client)}
                        onChange={(e) => handleClientSelection(preview.client, e.target.checked)}
                        className={`rounded ${isDark ? 'border-slate-600 bg-slate-800' : 'border-slate-300'} text-violet-600 focus:ring-violet-500 mr-3`}
                      />
                      <div>
                        <h5 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{preview.client}</h5>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          {preview.totalHours.toFixed(1)} hours • {formatCurrency(preview.totalAmount)}
                        </p>
                      </div>
                    </div>
                    <div className={`text-right text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      Invoice Date: {new Date(invoiceSettings.invoiceDate).toLocaleDateString()}
                      <br />
                      Due Date: {new Date(invoiceSettings.dueDate).toLocaleDateString()}
                    </div>
                  </div>

                  {invoiceSettings.groupByProject && (
                    <div className="mt-3">
                      <div className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Project Breakdown:</div>
                      <div className={`rounded-lg p-3 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                        {preview.projectBreakdown.map((project) => (
                          <div key={project.projectName} className="flex justify-between items-center py-1">
                            <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{project.projectName}</span>
                            <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {project.hours.toFixed(1)}h × {formatCurrency(project.rate)} = {formatCurrency(project.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className={`border rounded-xl p-4 ${isDark ? 'bg-violet-500/10 border-violet-500/30' : 'bg-violet-50 border-violet-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h4 className={`text-md font-medium ${isDark ? 'text-violet-300' : 'text-violet-900'}`}>Selected for Invoicing</h4>
                <p className={`text-sm ${isDark ? 'text-violet-400' : 'text-violet-700'}`}>
                  {selectedClients.size} client{selectedClients.size !== 1 ? 's' : ''} •
                  Total: {formatCurrency(
                    invoicePreviews
                      .filter(p => selectedClients.has(p.client))
                      .reduce((sum, p) => sum + p.totalAmount, 0)
                  )}
                </p>
              </div>
              <div className={`text-xs ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                Invoices will be created as drafts in Xero
              </div>
            </div>
          </div>
        </div>
      )}

      {!xeroConnection.isConnected && (
        <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          <svg className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <p>Connect to Xero to create draft invoices from your time entries</p>
        </div>
      )}
    </div>
  );
};

export default XeroIntegration;